// src/components/dashboard/YearlyFinanceOverview.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getRequest } from '../services/apiService';
import { DollarSign, TrendingUp, TrendingDown, Loader2, Filter, Download, FileImage, FileText, Image } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import html2canvas from 'html2canvas';

// Define chart colors (consistent with other charts if desired)
const FINANCE_COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444']; // Indigo, Green, Amber, Red

const FinanceSummaryCharts = ({ selectedProject, selectedShip }) => {
  const [yearlyData, setYearlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('2024-2025');
  const [selectedQuarter, setSelectedQuarter] = useState('q1');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [selectedDetailsProject, setSelectedDetailsProject] = useState(null);
  const [selectedDetailsShip, setSelectedDetailsShip] = useState(null);

  // Refs for capturing charts
  const chartsContainerRef = useRef(null);
  const detailsChartsRef = useRef(null);

  const years = ['2023-2024', '2024-2025', '2025-2026'];
  const quarters = ['q1', 'q2', 'q3', 'q4'];

  // Fetch projects for details modal
  useEffect(() => {
    getRequest('/master/projects/').then(res => {
      setProjects(res?.data || []);
    });
  }, []);

  // Fetch ships when project is selected in details modal
  useEffect(() => {
    if (!selectedDetailsProject) {
      setShips([]);
      return;
    }
    getRequest(`/master/projects/?parent_id=${selectedDetailsProject}`)
      .then(res => setShips(res?.data || []));
  }, [selectedDetailsProject]);

  // Fetch yearly overview data when filters change
  const fetchYearlyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch data for the selected project
      const response = await getRequest(
        `/transaction/finance/charts/?project_id=${selectedProject || ''}`
      );
      
      if (response && response.data) {
        // Transform the data into the expected format
        const transformedData = {
          years: ['2024-2025'], // Using current year as default
          mdl_fixed: [response.data.MDL?.fixed_budget || 0],
          mdl_variable: [response.data.MDL?.variable_budget || 0],
          grse_fixed: [response.data.GRSE?.fixed_budget || 0],
          grse_variable: [response.data.GRSE?.variable_budget || 0]
        };
        setYearlyData(transformedData);
      } else {
        setYearlyData(null);
        setError('No yearly finance data found.');
      }
    } catch (err) {
      setError('Failed to load yearly finance data.');
      setYearlyData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  // Fetch details data when filters change
  const fetchDetailsData = useCallback(async () => {
    if (!selectedDetailsProject) return;
    
    setDetailsLoading(true);
    setDetailsError(null);
    try {
      const response = await getRequest(
        `/transaction/finance/charts/?financial_year=${selectedYear}&quarter=${selectedQuarter}` +
        (selectedDetailsProject ? `&project_id=${selectedDetailsProject}` : '') +
        (selectedDetailsShip ? `&ship_id=${selectedDetailsShip}` : '')
      );
      
      if (response && response.data) {
        // Transform the data into the expected format
        const transformedData = {
          years: [selectedYear], // Use the selected year
          mdl_fixed: [response.data.MDL?.fixed_budget || 0],
          mdl_variable: [response.data.MDL?.variable_budget || 0],
          grse_fixed: [response.data.GRSE?.fixed_budget || 0],
          grse_variable: [response.data.GRSE?.variable_budget || 0]
        };
        setDetailsData(transformedData);
      } else {
        setDetailsData(null);
        setDetailsError('No finance data found for selected filters.');
      }
    } catch (err) {
      setDetailsError('Failed to load finance data.');
      setDetailsData(null);
    } finally {
      setDetailsLoading(false);
    }
  }, [selectedYear, selectedQuarter, selectedDetailsProject, selectedDetailsShip]);

  useEffect(() => {
    fetchYearlyData();
  }, [fetchYearlyData]);

  useEffect(() => {
    if (showDetailsModal) {
      fetchDetailsData();
    }
  }, [showDetailsModal, fetchDetailsData]);

  // Download functions
  const downloadCSV = (data, filename) => {
    if (!data) return;
    
    // Prepare CSV data
    const csvData = [];
    
    // Headers
    csvData.push(['Year', 'MDL Fixed (₹Cr)', 'MDL Variable (₹Cr)', 'GRSE Fixed (₹Cr)', 'GRSE Variable (₹Cr)']);
    
    // Data rows
    data.years.forEach((year, index) => {
      csvData.push([
        year,
        (data.mdl_fixed[index] || 0).toFixed(2),
        (data.mdl_variable[index] || 0).toFixed(2),
        (data.grse_fixed[index] || 0).toFixed(2),
        (data.grse_variable[index] || 0).toFixed(2)
      ]);
    });
    
    // Convert to CSV string
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadChartAsPNG = async (elementRef, filename) => {
    if (!elementRef.current) return;
    
    try {
      const canvas = await html2canvas(elementRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      
      const link = document.createElement('a');
      link.download = `${filename}_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error downloading chart as PNG:', error);
      alert('Failed to download chart as image. Please try again.');
    }
  };

  // --- Prepare Data for Display ---

  // Card Data (using the latest available year's data)
  const latestYearIndex = yearlyData?.years ? yearlyData.years.length - 1 : -1;
  const cardValues = latestYearIndex >= 0 ? {
    mdl_fixed: yearlyData.mdl_fixed[latestYearIndex] || 0,
    mdl_variable: yearlyData.mdl_variable[latestYearIndex] || 0,
    grse_fixed: yearlyData.grse_fixed[latestYearIndex] || 0,
    grse_variable: yearlyData.grse_variable[latestYearIndex] || 0,
  } : { mdl_fixed: 0, mdl_variable: 0, grse_fixed: 0, grse_variable: 0 };

  const cardInfo = [
      { label: 'MDL Fixed (Latest Year)', key: 'mdl_fixed', icon: <DollarSign className="text-blue-600 mr-2" />, border: 'border-blue-600' },
      { label: 'MDL Variable (Latest Year)', key: 'mdl_variable', icon: <TrendingUp className="text-green-600 mr-2" />, border: 'border-green-600' },
      { label: 'GRSE Fixed (Latest Year)', key: 'grse_fixed', icon: <TrendingDown className="text-amber-500 mr-2" />, border: 'border-amber-500' },
      { label: 'GRSE Variable (Latest Year)', key: 'grse_variable', icon: <TrendingUp className="text-purple-600 mr-2" />, border: 'border-purple-600' }
  ];

  // Bar Chart Data (mapping years to values)
  const barChartData = yearlyData?.years?.map((year, i) => ({
    year,
    'MDL Fixed': Number(yearlyData.mdl_fixed?.[i] || 0),
    'MDL Variable': Number(yearlyData.mdl_variable?.[i] || 0),
    'GRSE Fixed': Number(yearlyData.grse_fixed?.[i] || 0),
    'GRSE Variable': Number(yearlyData.grse_variable?.[i] || 0)
  })) || [];

  // Pie Chart Data (summing up all years for each category)
  const sumArray = (arr) => arr?.reduce((acc, val) => acc + Number(val || 0), 0) || 0;
  const pieChartData = yearlyData ? [
    { name: 'MDL Fixed', value: sumArray(yearlyData.mdl_fixed) },
    { name: 'MDL Variable', value: sumArray(yearlyData.mdl_variable) },
    { name: 'GRSE Fixed', value: sumArray(yearlyData.grse_fixed) },
    { name: 'GRSE Variable', value: sumArray(yearlyData.grse_variable) }
  ].filter(item => item.value > 0) : [];

  const totalOverallExpenditure = pieChartData.reduce((sum, item) => sum + item.value, 0);

  // Custom label for Pie chart
  const renderCustomizedLabel = ({
    cx, cy, midAngle, innerRadius, outerRadius, percent, index,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#333"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={16}
        fontWeight={600}
      >
        {percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''}
      </text>
    );
  };

  // --- Details Modal ---
  const DetailsModal = () => {
    // Reset filters when modal closes
    const handleHide = () => {
      setShowDetailsModal(false);
      setSelectedDetailsProject('');
      setSelectedDetailsShip('');
      setDetailsData(null);
      setDetailsError(null);
    };

    if (!showDetailsModal) return null;

    return (
      <Dialog
        visible={showDetailsModal}
        header={<div className="text-lg font-bold text-indigo-700 tracking-wide">Finance Details</div>}
        className="max-w-4xl w-full rounded-xl"
        onHide={handleHide}
        style={{ borderRadius: 16 }}
        contentStyle={{ borderRadius: 16, background: '#f6f8fa', padding: 0 }}
      >
        <div className="p-6 space-y-6 bg-white rounded-xl">
          {/* Filters and Download Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Project</label>
              <select
                value={selectedDetailsProject || ''}
                onChange={(e) => setSelectedDetailsProject(e.target.value)}
                className="w-full text-base border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all font-medium shadow-sm"
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Ship</label>
              <select
                value={selectedDetailsShip || ''}
                onChange={(e) => setSelectedDetailsShip(e.target.value)}
                className="w-full text-base border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all font-medium shadow-sm"
                disabled={!selectedDetailsProject}
              >
                <option value="">All Ships</option>
                {ships.map(ship => (
                  <option key={ship.id} value={ship.id}>{ship.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col justify-end gap-2">
              <button
                onClick={() => downloadCSV(detailsData, 'finance_details')}
                disabled={!detailsData}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 text-white focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Download className="mr-2 h-4 w-4" />
                CSV
              </button>
              <button
                onClick={() => downloadChartAsPNG(detailsChartsRef, 'finance_details_chart')}
                disabled={!detailsData}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <FileImage className="mr-2 h-4 w-4" />
                PNG
              </button>
            </div>
          </div>

          {/* Charts */}
          <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 p-4 shadow-sm" ref={detailsChartsRef}>
            {detailsLoading ? (
              <div className="flex items-center justify-center h-64 text-indigo-500 animate-pulse transition-all duration-300">
                <Loader2 className="animate-spin mr-2" size={24} /> Loading Finance Details...
              </div>
            ) : detailsError ? (
              <div className="flex items-center justify-center h-64 text-red-500 text-base p-4 text-center transition-all duration-300">
                {detailsError}
              </div>
            ) : !detailsData ? (
              <div className="flex items-center justify-center h-64 text-gray-400 text-base transition-all duration-300">
                No data available for selected filters.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bar Chart */}
                <div className="bg-white p-4 rounded-lg shadow-sm h-80 flex flex-col justify-center">
                  <h3 className="text-base font-semibold mb-4 text-indigo-800 text-center">Yearly Expenditure</h3>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={detailsData.years.map((year, i) => ({
                      year,
                      'MDL Fixed': Number(detailsData.mdl_fixed?.[i] || 0),
                      'MDL Variable': Number(detailsData.mdl_variable?.[i] || 0),
                      'GRSE Fixed': Number(detailsData.grse_fixed?.[i] || 0),
                      'GRSE Variable': Number(detailsData.grse_variable?.[i] || 0)
                    }))} margin={{ top: 5, right: 5, left: -20, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${value}Cr`}/>
                      <Tooltip formatter={(value) => `₹${Number(value).toFixed(2)}Cr`} contentStyle={{ fontSize: '13px' }}/>
                      <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} iconSize={12} verticalAlign="bottom"/>
                      <Bar dataKey="MDL Fixed" fill={FINANCE_COLORS[0]} radius={[8, 8, 0, 0]} />
                      <Bar dataKey="MDL Variable" fill={FINANCE_COLORS[1]} radius={[8, 8, 0, 0]} />
                      <Bar dataKey="GRSE Fixed" fill={FINANCE_COLORS[2]} radius={[8, 8, 0, 0]} />
                      <Bar dataKey="GRSE Variable" fill={FINANCE_COLORS[3]} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Doughnut Chart */}
                <div className="bg-white p-4 rounded-lg shadow-sm h-80 flex flex-col justify-center">
                  <h3 className="text-base font-semibold mb-4 text-indigo-800 text-center">Expenditure Breakdown</h3>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'MDL Fixed', value: sumArray(detailsData.mdl_fixed) },
                          { name: 'MDL Variable', value: sumArray(detailsData.mdl_variable) },
                          { name: 'GRSE Fixed', value: sumArray(detailsData.grse_fixed) },
                          { name: 'GRSE Variable', value: sumArray(detailsData.grse_variable) }
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        innerRadius={48}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                      >
                        {FINANCE_COLORS.map((color, index) => (
                          <Cell key={color} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₹${Number(value).toFixed(2)}Cr`} contentStyle={{ fontSize: '13px' }}/>
                      <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} iconSize={12}/>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    );
  };

  return (
    <div className="space-y-4 px-4">
      {/* Filters and Download Buttons */}
      <div className="flex justify-between items-center">
        {/* <div className="flex flex-nowrap items-center gap-3 bg-indigo-50 shadow-md rounded-lg px-2 mb-2">
          <Filter size={48} className="text-indigo-500" />
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(e.target.value)} 
            className="text-base border border-gray-200 rounded-lg px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all font-medium"
            title="Select Year"
            style={{ minWidth: 120 }}
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select 
            value={selectedQuarter} 
            onChange={(e) => setSelectedQuarter(e.target.value)} 
            className="text-base border border-gray-200 rounded-lg px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all font-medium"
            title="Select Quarter"
            style={{ minWidth: 90 }}
          >
            {quarters.map(q => (
              <option key={q} value={q}>{q.toUpperCase()}</option>
            ))}
          </select>
        </div> */}
        <div className="flex gap-2">
          <button
            onClick={() => downloadCSV(yearlyData, 'yearly_finance_overview')}
            disabled={!yearlyData}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
              title="Download CSV"
            >
              <FileText size={16} className="mr-1" />
              CSV
          </button>
          <button
            onClick={() => downloadChartAsPNG(chartsContainerRef, 'yearly_finance_charts')}
            disabled={!yearlyData}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
            title="Download PNG"
          >
            <Image size={16} className="mr-1" />
            PNG
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-72 text-gray-500">
          <Loader2 className="animate-spin mr-2" size={20} /> Loading Yearly Finance Overview...
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-72 text-red-500 text-sm p-4 text-center">
          {error}
        </div>
      ) : !yearlyData ? (
        <div className="flex items-center justify-center h-72 text-gray-500 text-sm">
          No data available for yearly finance overview.
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cardInfo.map((item, idx) => {
                const value = cardValues[item.key];
                return (
                  <div key={item.label} className={`bg-white p-4 rounded-xl shadow-md border-l-4 ${item.border}`}>
                    <div className="text-sm text-gray-500 mb-1">{item.label}</div>
                    <div className="flex items-center">
                      {item.icon}
                      <span className="text-2xl font-bold text-gray-800">
                       {`₹${Number(value).toFixed(2)}Cr`}
                      </span>
                    </div>
                  </div>
                );
            })}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" ref={chartsContainerRef}>
            {/* Bar Chart */}
            <div className="bg-white p-4 md:p-6 rounded-xl shadow-md h-96 w-auto">
              <h3 className="text-lg font-semibold mb-4 text-indigo-800 text-center">Yearly Expenditure</h3>
              <ResponsiveContainer width="110%" height="100%">
                <BarChart data={barChartData} margin={{ top: 5, right: 5, left: -20, bottom: 25, radius: 8 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" />
                  <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 7 }} tickFormatter={(value) => `₹${value}Cr`}/>
                  <Tooltip formatter={(value) => `₹${Number(value).toFixed(2)}Cr`} contentStyle={{ fontSize: '12px' }}/>
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} iconSize={10} verticalAlign="bottom"/>
                  <Bar dataKey="MDL Fixed" fill={FINANCE_COLORS[0]} radius={[8, 8, 0, 0]} />
                  <Bar dataKey="MDL Variable" fill={FINANCE_COLORS[1]} radius={[8, 8, 0, 0]} />
                  <Bar dataKey="GRSE Fixed" fill={FINANCE_COLORS[2]} radius={[8, 8, 0, 0]} />
                  <Bar dataKey="GRSE Variable" fill={FINANCE_COLORS[3]} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Pie Chart */}
            <div className=''>
            <div className="bg-white p-4 md:p-6 rounded-xl h-96 shadow-md w-auto">
              <h3 className="text-lg font-semibold mb-4 text-indigo-800 text-center">Overall Expenditure by Category</h3>

              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius="80%"
                    innerRadius="40%"
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={FINANCE_COLORS[index % FINANCE_COLORS.length]} stroke="#fff" strokeWidth={1}/>
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`₹${Number(value).toFixed(2)}Cr (${totalOverallExpenditure > 0 ? ((value / totalOverallExpenditure) * 100).toFixed(1) : 0}%)`, name]} contentStyle={{ fontSize: '12px' }}/>
                  <Legend layout="vertical" verticalAlign="left" align="left" wrapperStyle={{ fontSize: '11px' }} iconSize={10}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
            </div>
          </div>
        </>
      )}

      {/* Add Details Modal */}
      <DetailsModal />
    </div>
  );
};

export default FinanceSummaryCharts;